import { useMemo } from 'react'
import ColorHash from 'color-hash'
import { cn } from '@/lib/utils'

const textColorHash = new ColorHash({
  lightness: 0.2,
})
const bgColorHash = new ColorHash({
  lightness: 0.9,
})

type CategoryBadgeProps = {
  className?: string
  title?: string
  size?: 'sm' | 'base'
  /** 'soundReview': 色をハッシュで決めず、サウンドレビュー固定のオレンジにする */
  tone?: 'hash' | 'soundReview'
}

// ハッシュ色と同じ「淡い背景 + 濃い文字（ダークは反転）」の構成に揃える。
// bg-orange-400 + 白文字はコントラスト比が 2.3:1 程度で AA を満たさないため使わない
const SOUND_REVIEW_TONE_CLASS
  = 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-white'

const SIZE_CLASS = {
  sm: 'px-2 py-1 text-xs',
  base: 'px-3 py-1.5 text-sm',
} as const

const BASE_CLASS = 'inline-flex items-center rounded-md font-semibold'

export function CategoryBadge({
  className, title = '', size = 'sm', tone = 'hash',
}: CategoryBadgeProps) {
  const textColor = useMemo(() => textColorHash.hex(title), [ title ])
  const bgColor = useMemo(() => bgColorHash.hex(title), [ title ])

  if (!title) {
    return null
  }

  if (tone === 'soundReview') {
    return (
      <div className={cn(BASE_CLASS, SIZE_CLASS[size], SOUND_REVIEW_TONE_CLASS, className)}>
        {title}
      </div>
    )
  }

  return (
    <>
      <style jsx>{`
        .badge {
          background: ${bgColor};
          color: ${textColor};
        }
        :global(.dark) .badge {
          color: ${bgColor};
          background: ${textColor};
        }
      `}</style>
      <div className={cn('badge', BASE_CLASS, SIZE_CLASS[size], className)}>
        {title}
      </div>
    </>
  )
}

type CategoryBadgeListProps = {
  categoryList: string[]
  className?: string
  size?: 'sm' | 'base'
}

export function CategoryBadgeList({ categoryList, className, size }: CategoryBadgeListProps) {
  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {categoryList.map((category) => (
        <CategoryBadge key={category} title={category} size={size} />
      ))}
    </div>
  )
}
